'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FirebaseIndexErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  indexUrl?: string;
  errorMessage?: string;
}

export function FirebaseIndexErrorDialog({
  open,
  onOpenChange,
  indexUrl,
  errorMessage,
}: FirebaseIndexErrorDialogProps) {
  const handleCreateIndex = () => {
    if (indexUrl) {
      window.open(indexUrl, '_blank', 'noopener,noreferrer');
    }
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
            <AlertDialogTitle>Firebase Index Required</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3">
            <p>
              This operation requires a Firebase database index to be created. 
              This is a one-time setup that improves query performance.
            </p>
            
            {indexUrl ? (
              <div className="bg-muted p-4 rounded-md space-y-2">
                <p className="font-semibold text-sm text-foreground">
                  To create the required index:
                </p>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Click the "Create Index" button below</li>
                  <li>Sign in to Firebase Console if prompted</li>
                  <li>Click "Create Index" in the Firebase console</li>
                  <li>Wait a few minutes for the index to build</li>
                  <li>Try the operation again</li>
                </ol>
              </div>
            ) : (
              <div className="bg-muted p-4 rounded-md">
                <p className="text-sm font-mono text-foreground break-all">
                  {errorMessage || 'An index is required for this query.'}
                </p>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> You need Firebase project admin access to create indexes. 
              If you don't have access, contact your project administrator.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {indexUrl && (
            <Button onClick={handleCreateIndex} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Create Index in Firebase
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
